import { useEffect, useState } from 'react'
import DashboardCard from '../shared/DashboardCard'
import StatCard from '../shared/StatCard'
import TrendLine from '../../charts/TrendLine'
import { useAuth } from '../../../hooks/useAuth'
import { apiService, type AthleteAnalytics } from '../../../services/api'
import { TrophyIcon, CalendarIcon, ChartBarIcon, StarIcon } from '@heroicons/react/24/outline'

const UNAVAILABLE = '—'

/**
 * Time units, where a *lower* value is the better one. Matched exactly after
 * normalising — a substring test cannot be used, because `s` is a substring of
 * almost everything and `in` (inches) is a substring of `min`. Mirrors
 * `isLowerBetter` in useAnalyticsSummary.ts; both go away when the backend
 * sends an explicit `lowerIsBetter` flag.
 */
const TIME_UNITS = new Set([
  's',
  'sec',
  'secs',
  'second',
  'seconds',
  'ms',
  'msec',
  'millisecond',
  'milliseconds',
  'min',
  'mins',
  'minute',
  'minutes',
])

function isLowerBetter(drillName: string, unit: string): boolean {
  const normalizedUnit = unit.trim().toLowerCase().replace(/\.$/, '')
  if (TIME_UNITS.has(normalizedUnit) || normalizedUnit.includes('sec')) {
    return true
  }
  const name = drillName.toLowerCase()
  return name.includes('time') || name.includes('dash') || name.includes('shuttle')
}

const getPercentileColor = (percentile: number) => {
  if (percentile >= 90) {
    return 'text-green-600 bg-green-50'
  }
  if (percentile >= 75) {
    return 'text-blue-600 bg-blue-50'
  }
  if (percentile >= 50) {
    return 'text-yellow-600 bg-yellow-50'
  }
  return 'text-gray-600 bg-gray-50'
}

/** Formats a rank as "87th", handling the 11/12/13 exceptions. */
function ordinal(value: number): string {
  const rounded = Math.round(value)
  const lastTwo = rounded % 100
  if (lastTwo >= 11 && lastTwo <= 13) {
    return `${rounded}th`
  }
  switch (rounded % 10) {
    case 1:
      return `${rounded}st`
    case 2:
      return `${rounded}nd`
    case 3:
      return `${rounded}rd`
    default:
      return `${rounded}th`
  }
}

export default function AthleteDashboard() {
  const { user } = useAuth()
  const userId = user?.id ?? null

  const [analytics, setAnalytics] = useState<AthleteAnalytics | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [unidentified, setUnidentified] = useState(false)

  useEffect(() => {
    if (!userId) {
      return
    }

    let cancelled = false

    const load = async () => {
      setLoading(true)
      setError(null)
      setUnidentified(false)

      // `GET /api/athletes/me` resolves the caller's own athlete record from
      // the token alone. It replaced a client-side heuristic that listed
      // /api/athletes narrowed by the user's last name and matched `userId`
      // — exact, so it could never surface the wrong athlete, but it degraded
      // silently for anyone whose profile did not narrow that way (#159).
      const meResponse = await apiService
        .getMyAthlete()
        .catch(() => ({ error: 'Network Error', data: undefined, status: undefined }))

      if (cancelled) {
        return
      }

      // A 404 is the route saying this account has no athlete profile linked —
      // a legitimate state for a parent or an unlinked athlete, and distinct
      // from a failure to read.
      if (meResponse.status === 404) {
        setAnalytics(null)
        setUnidentified(true)
        setLoading(false)
        return
      }

      if (!meResponse.data) {
        // No fallback content. Every figure on this page used to be a literal
        // (8 events, 87th percentile, 12 PRs, a four-point 40-yard trend); an
        // unreadable API must now look unreadable (#159).
        setAnalytics(null)
        setError('Failed to load your performance data')
        setLoading(false)
        return
      }

      const response = await apiService
        .getAthleteAnalytics(meResponse.data.id, { timeRange: 'all' })
        .catch(() => ({ error: 'Network Error', data: undefined }))

      if (cancelled) {
        return
      }

      if (response.data) {
        setAnalytics(response.data)
      } else {
        setAnalytics(null)
        setError('Failed to load your performance data')
      }

      setLoading(false)
    }

    load()

    return () => {
      cancelled = true
    }
  }, [userId])

  const drills = analytics?.drillPerformance ?? []

  // The drill with the most recorded results is the one whose trend is worth
  // charting. Its `results[]` already carry {value, date, eventName}, so no
  // separate trend request is needed.
  const primaryDrill = drills.reduce<(typeof drills)[number] | null>(
    (best, drill) => (best === null || drill.results.length > best.results.length ? drill : best),
    null
  )

  const trendData = (primaryDrill?.results ?? [])
    .slice()
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    .map(result => ({
      date: result.date,
      value: result.value,
      eventName: result.eventName,
    }))

  // Most recent result per drill, newest first.
  const recentResults = drills
    .map(drill => {
      const latest = drill.results
        .slice()
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0]
      return latest ? { drill, latest } : null
    })
    .filter(
      (
        entry
      ): entry is {
        drill: (typeof drills)[number]
        latest: (typeof drills)[number]['results'][number]
      } => entry !== null
    )
    .sort((a, b) => new Date(b.latest.date).getTime() - new Date(a.latest.date).getTime())
    .slice(0, 6)

  const athleteStats = [
    {
      name: 'Events Participated',
      value: analytics ? analytics.performanceSummary.totalEvents : UNAVAILABLE,
      icon: CalendarIcon,
    },
    {
      name: 'Results Recorded',
      value: analytics ? analytics.performanceSummary.totalResults : UNAVAILABLE,
      icon: ChartBarIcon,
    },
    {
      // `positionRanking` is optional in the route's response schema — the
      // backend omits it when the athlete has no recorded position, or no
      // valid results to be ranked on. Absent must render as "—", never as a
      // number: it once rendered "105th" from a rank the API had invented.
      //
      // When present, the label names the position the percentile was measured
      // within ("QB Percentile"), so the tile never asserts a cohort the
      // response did not supply. The generic label is only used when there is
      // no percentile to describe.
      name: analytics?.positionRanking
        ? `${analytics.positionRanking.position} Percentile`
        : 'Position Percentile',
      value: analytics?.positionRanking
        ? ordinal(analytics.positionRanking.percentile)
        : UNAVAILABLE,
      icon: TrophyIcon,
    },
    {
      // Renamed from "Personal Records". The endpoint exposes a personal best
      // *per drill*, not a count of records broken, and labelling the drill
      // count "Personal Records" would describe a different quantity than the
      // number shown.
      name: 'Drills with a Personal Best',
      value: analytics ? drills.length : UNAVAILABLE,
      icon: StarIcon,
    },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-gray-900">My Performance Dashboard</h1>
        <p className="mt-1 text-sm text-gray-600">
          Track your progress and improve your athletic performance
        </p>
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 p-4" role="alert">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {unidentified && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-4" role="status">
          <p className="text-sm text-amber-800">
            <span className="font-medium">
              We could not find an athlete profile linked to your account.
            </span>{' '}
            Ask your coach or event organizer to link one, and your results will appear here.
          </p>
        </div>
      )}

      {/* Performance Stats */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {athleteStats.map((stat, index) => (
          <StatCard
            key={index}
            name={stat.name}
            value={stat.value}
            icon={stat.icon}
            loading={loading}
          />
        ))}
      </div>

      {/* Primary Drill Trend */}
      <DashboardCard
        title={primaryDrill ? `${primaryDrill.drill.name} Trend` : 'Performance Trend'}
        loading={loading}
        error={error ?? undefined}
      >
        {trendData.length < 2 ? (
          <p className="py-4 text-center text-sm text-gray-500">
            Not enough results yet to chart a trend. Two or more results in the same drill will show
            one here.
          </p>
        ) : (
          <TrendLine
            data={trendData}
            unit={primaryDrill?.drill.unit}
            lowerIsBetter={isLowerBetter(
              primaryDrill?.drill.name ?? '',
              primaryDrill?.drill.unit ?? ''
            )}
          />
        )}
      </DashboardCard>

      {/* Recent Results */}
      <DashboardCard title="Recent Results" loading={loading} error={error ?? undefined}>
        {recentResults.length === 0 ? (
          <p className="py-4 text-center text-sm text-gray-500">No results recorded yet.</p>
        ) : (
          <div className="space-y-4">
            {recentResults.map(({ drill, latest }) => (
              <div
                key={drill.drill.id}
                className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <h4 className="text-sm font-medium text-gray-900">{drill.drill.name}</h4>
                      {latest.value === drill.personalBest && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                          <StarIcon className="h-3 w-3 mr-1" />
                          PB
                        </span>
                      )}
                    </div>
                    <div className="flex items-center space-x-4 mt-1">
                      <span className="text-lg font-semibold text-gray-900">
                        {latest.value} {drill.drill.unit}
                      </span>
                      <span className="text-xs text-gray-500">{latest.eventName}</span>
                      <span className="text-xs text-gray-500">
                        {new Date(latest.date).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  {/*
                    The percentile is optional per result in the response
                    schema. When it is absent nothing is rendered — there is no
                    default value that would not be an invention.
                  */}
                  {latest.percentile !== undefined && (
                    <div className="text-right">
                      <div
                        className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${getPercentileColor(latest.percentile)}`}
                      >
                        {ordinal(latest.percentile)} percentile
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </DashboardCard>

      {/*
        Three cards were removed from this dashboard, all of them literals with
        nothing behind them (#159):

        - "Upcoming Events" listed two invented events with a "registered" /
          "invited" status. `GET /api/events` can list events, but no endpoint
          reports THIS athlete's registration status for them, and the status
          badge was the whole point of the card.
        - "Personalized Training Recommendations" printed three pieces of
          invented coaching advice ("Improve Acceleration", "Enhance Upper Body
          Power"). Nothing generates these. Real, coach-authored plans live at
          /training via `GET /api/training/plans/:athleteId`.
        - The percentile scope toggle (national / state / district) drove
          nothing — the stats beside it were constants. Wiring it for real needs
          `GET /api/analytics/percentile/:athleteId`, which 400s for the state
          and district scopes unless the caller supplies a `scopeId` that the
          client has no way to obtain.
      */}
    </div>
  )
}
